import {type DocumentNode, gql} from '@apollo/client';
import {type Filter, GqlRequestBuilder} from '@igp/shared/api';
import type {BoUser, QueryGetBoUsersArgs} from '@igp/shared/contract/pam';
import type {Path} from '@igp/shared/utils';
import type {EntityType} from '@models';

import type {BoUserQueryFields, BoUserServerFilterKeys, BoUserTextFilterKeys} from './types';

export class BoUserRequestBuilder extends GqlRequestBuilder<EntityType, QueryGetBoUsersArgs, BoUserQueryFields, BoUserServerFilterKeys> {
    public getAddMutation(): DocumentNode {
        return gql`
            mutation AddBoUser($bo_user: BoUserInput!) {
                addBoUser(bo_user: $bo_user)
            }
        `;
    }

    public getUpdateMutation(): DocumentNode {
        return gql`
            mutation UpdateBoUser($bo_user_id: String!, $bo_user: BoUserInput!) {
                updateBoUser(bo_user_id: $bo_user_id, bo_user: $bo_user)
            }
        `;
    }

    public getResetPasswordMutation(): DocumentNode {
        return gql`
            mutation ResetPassword($bo_user_id: String!, $credentials: BoUserCredentialsInput!) {
                resetPassword(bo_user_id: $bo_user_id, credentials: $credentials)
            }
        `;
    }

    public getUpdateAccountStatusMutation(): DocumentNode {
        return gql`
            mutation UpdateAccountStatus($bo_user_id: String!, $is_enabled: Boolean!) {
                updateAccountStatus(bo_user_id: $bo_user_id, is_enabled: $is_enabled)
            }
        `;
    }

    public getUnlinkAgentMutation(): DocumentNode {
        return gql`
            mutation UnlinkAgent($bo_user_id: String!, $uid: String!) {
                unlinkAgent(bo_user_id: $bo_user_id, uid: $uid)
            }
        `;
    }

    protected validateFilters(_filter: Partial<Record<BoUserServerFilterKeys, unknown>>): void {
        return;
    }

    protected buildQuery(fields: BoUserQueryFields[] = []): DocumentNode {
        const requiredFields: BoUserQueryFields[] = ['id'];
        const groupFields: BoUserQueryFields[] = ['roles', 'aal_group'];
        const queryFieldsWithoutGroupFields = fields.filter(field => !groupFields.includes(field)) ?? [];
        const queryFields: string[] = [...new Set([...requiredFields, ...queryFieldsWithoutGroupFields])];
        if (queryFieldsWithoutGroupFields.length !== fields.length) {
            queryFields.push('groups.id', 'groups.name');
        }
        return gql`
            query GetBoUsers($filter: BoUserFilter, $sort: Sorting, $start: Int, $end: Int) {
                getBoUsers(filter: $filter, sort: $sort, end: $end, start: $start) {
                    items {
                        ${this.buildQueryFields(queryFields)}
                    }
                    total
                }
            }
        `;
    }

    protected buildFilter(filter: Filter<BoUserServerFilterKeys>): Pick<QueryGetBoUsersArgs, 'filter'> {
        return {
            filter: {
                group_ids: this.toGQLMultiselectFilter(filter, 'group_ids'),
                aal_group_ids: this.toGQLMultiselectFilter(filter, 'aal_group_ids'),
                role_group_ids: this.toGQLMultiselectFilter(filter, 'role_group_ids'),
                user_id: this.toGQLStringFilter(filter, 'uuid'),
                text: this.getGQLTextFilter(
                    Object.keys(this.filterFieldsMapper).map((key: BoUserTextFilterKeys) =>
                        this.toGQLTextFilter(this.filterFieldsMapper[key], filter[key] as string)
                    )
                ),
            },
        };
    }

    private readonly filterFieldsMapper: Record<BoUserTextFilterKeys, Path<BoUser>[]> = {
        all: ['username', 'first_name', 'last_name', 'email', 'attributes.player_id'],
        username: ['username'],
        first_name: ['first_name'],
        last_name: ['last_name'],
        email: ['email'],
        player_id: ['attributes.player_id'],
    };
}
